import { ref, computed } from 'vue'
import apiClient from '../api/client'

const accessToken = ref(localStorage.getItem('access_token'))
const refreshToken = ref(localStorage.getItem('refresh_token'))
const user = ref(null)

export function useAuth() {
  const isAuthenticated = computed(() => !!accessToken.value)

  const setTokens = (access, refresh) => {
    accessToken.value = access
    refreshToken.value = refresh
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)
  }

  const clearTokens = () => {
    accessToken.value = null
    refreshToken.value = null
    user.value = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  const login = async (email, password) => {
    try {
      const response = await apiClient.post('/api/auth/login', {
        email,
        password,
      })
      const { access, refresh } = response.data
      setTokens(access, refresh)
      await fetchCurrentUser()
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      }
    }
  }

  const register = async (email, password, passwordConfirm) => {
    try {
      const response = await apiClient.post('/api/auth/register', {
        email,
        password,
        password_confirm: passwordConfirm,
      })
      const { access, refresh } = response.data
      setTokens(access, refresh)
      await fetchCurrentUser()
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
      }
    }
  }

  const logout = () => {
    clearTokens()
  }

  const fetchCurrentUser = async () => {
    try {
      const response = await apiClient.get('/api/auth/me')
      user.value = response.data
      return { success: true }
    } catch (error) {
      clearTokens()
      return { success: false }
    }
  }

  const getTokenFromSession = async () => {
    try {
      // This is called after Google OAuth redirect
      const response = await apiClient.get('/api/auth/token')
      const { access, refresh } = response.data
      setTokens(access, refresh)
      await fetchCurrentUser()
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get token',
      }
    }
  }

  const initAuth = async () => {
    if (accessToken.value) {
      await fetchCurrentUser()
    }
  }

  const requestPasswordReset = async (email) => {
    try {
      const response = await apiClient.post('/api/auth/password-reset/request', {
        email,
      })
      return {
        success: true,
        message: response.data.message,
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to request password reset',
      }
    }
  }

  const verifyPasswordResetToken = async (token) => {
    try {
      const response = await apiClient.post('/api/auth/password-reset/verify', {
        token,
      })
      return {
        success: true,
        message: response.data.message,
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Invalid or expired token',
      }
    }
  }

  const confirmPasswordReset = async (token, newPassword, newPasswordConfirm) => {
    try {
      const response = await apiClient.post('/api/auth/password-reset/confirm', {
        token,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      })
      return {
        success: true,
        message: response.data.message,
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset password',
      }
    }
  }

  const changePassword = async (currentPassword, newPassword, newPasswordConfirm) => {
    try {
      const response = await apiClient.post('/api/auth/password-change', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      })
      return {
        success: true,
        message: response.data.message,
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to change password',
      }
    }
  }

  return {
    isAuthenticated,
    user,
    login,
    register,
    logout,
    fetchCurrentUser,
    getTokenFromSession,
    initAuth,
    setTokens,
    requestPasswordReset,
    verifyPasswordResetToken,
    confirmPasswordReset,
    changePassword,
  }
}
